import React, { useState, useEffect } from "react";
import { X, Search, Edit } from "lucide-react";
import EditPartModal from "./EditPartModal";
import ImageLightbox from "./ImageLightbox";
import { usePageConfig } from "../hooks/usePageConfig";
import { buildMediaUrl } from "../utils/mediaUrl";
import {
  partFrames,
  damageTypeLabel,
  partActionLabel,
} from "../utils/partPresentation";

const DamagedPartsModal = ({
  isOpen,
  onClose,
  assessmentData,
  initialFilter = "all",
  initialSeverityFilter = "",
  onRefreshAssessment,
  isSearchActive,
  setIsSearchActive,
  isApproved = false,
}) => {
  const { clientConfig } = usePageConfig();
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [activeSeverityFilter, setActiveSeverityFilter] = useState(
    initialSeverityFilter
  );
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxCurrentIndex, setLightboxCurrentIndex] = useState(0);

  const mediaBaseUrl = clientConfig.mediaBaseUrl;

  const getMediaUrl = (path) =>
    buildMediaUrl(path, {
      baseUrl: mediaBaseUrl,
      pathPrefix: clientConfig.mediaPathPrefix,
    });

  // Every frame for the part, damage crops first — see partFrames. This used to
  // show damage_images OR part_images, so a panel photographed from five angles
  // showed the one crop that happened to carry the assessment.
  const getPartPreviewImages = (part) =>
    partFrames(part).map((frame, index) => ({
      id: `${part.part_name || "part"}-${index}`,
      imageUrl: getMediaUrl(frame.path),
      title: part.part_name || `Image ${index + 1}`,
      side: frame.side,
      source: "part",
    }));

  const openImageLightbox = (part, startIndex) => {
    const previewImages = getPartPreviewImages(part);
    if (previewImages.length === 0) return;

    setLightboxImages(previewImages);
    setLightboxCurrentIndex(startIndex);
    setLightboxOpen(true);
  };

  const closeImageLightbox = () => {
    setLightboxOpen(false);
    setLightboxImages([]);
    setLightboxCurrentIndex(0);
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Update filters when modal opens with new props
      setActiveFilter(initialFilter);
      setActiveSeverityFilter(initialSeverityFilter);
    }
  }, [isOpen, initialFilter, initialSeverityFilter]);

  const handleClose = () => {
    setIsAnimating(false);
    setIsSearchActive(false); // Reset search state
    setSearchQuery(""); // Clear search query
    setTimeout(() => {
      onClose();
    }, 300); // Wait for animation to complete
  };

  const handleEditPart = (part) => {
    setSelectedPart(part);
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedPart(null);
  };

  // Get vehicle image from uploads object (prioritizing front-left → front-right → any side)
  const getVehicleImageFromUploads = (assessmentData) => {
    const baseUrl = clientConfig.mediaBaseUrl;

    console.log("DamagedPartsModal - Vehicle image debug:", {
      hasAssessmentData: !!assessmentData,
      hasUploads: !!assessmentData?.uploads,
      uploadsLength: assessmentData?.uploads?.length || 0,
      uploads: assessmentData?.uploads,
    });

    if (!assessmentData?.uploads || assessmentData.uploads.length === 0) {
      console.log("No uploads found, returning null");
      return null;
    }

    // Helper function to get image URL from upload object
    const getImageUrl = (upload) => {
      const imagePath = upload.upload_picture || upload.picture;
      console.log("Processing upload:", upload, "imagePath:", imagePath);
      if (imagePath) {
        // Ensure the path starts with /
        const normalizedPath = imagePath.startsWith("/")
          ? imagePath
          : "/" + imagePath;
        const fullUrl = baseUrl + normalizedPath;
        console.log("Constructed vehicle image URL:", fullUrl);
        return fullUrl;
      }
      return null;
    };

    // Define priority order for vehicle sides
    const priorityOrder = [
      "front-left",
      "front-right",
      "front",
      "left",
      "right",
      "rear",
      "rear-left",
      "rear-right",
    ];

    // First, try to find images by priority order
    for (const side of priorityOrder) {
      const upload = assessmentData.uploads.find(
        (upload) => upload.side?.toLowerCase() === side.toLowerCase()
      );
      if (upload) {
        console.log(`Found upload for side ${side}:`, upload);
        const imageUrl = getImageUrl(upload);
        if (imageUrl) {
          console.log(`Using vehicle image from side ${side}:`, imageUrl);
          return imageUrl;
        }
      }
    }

    // If no specific side found, get any upload with a valid image
    for (const upload of assessmentData.uploads) {
      const imageUrl = getImageUrl(upload);
      if (imageUrl) {
        console.log("Using fallback vehicle image:", imageUrl);
        return imageUrl;
      }
    }

    console.log("No valid vehicle image found");
    return null;
  };
  if (!isOpen) return null;

  // Get assessment details
  const assessmentDetails = assessmentData?.assessment_details || [];

  // Get non-identified parts list from summary
  const nonIdentifiedPartsList =
    assessmentData?.summary?.non_identified_parts_list || [];

  // Helper function to get intensity from various possible locations
  const getIntensity = (part) => {
    // Check direct intensity field (capital I)
    if (part.Intensity) return parseInt(part.Intensity);
    if (part.intensity) return parseInt(part.intensity);
    if (part.damage_intensity) return parseInt(part.damage_intensity);
    if (part.severity) return parseInt(part.severity);
    if (part.damage_severity) return parseInt(part.damage_severity);

    // Check in damage_images array
    if (part.damage_images && part.damage_images.length > 0) {
      for (const img of part.damage_images) {
        if (img.Intensity) return parseInt(img.Intensity);
        if (img.intensity) return parseInt(img.intensity);
        if (img.damage_intensity) return parseInt(img.damage_intensity);
        if (img.severity) return parseInt(img.severity);
        if (img.damage_severity) return parseInt(img.damage_severity);
      }
    }

    return 0;
  };

  /**
   * Severity for filtering and counting.
   *
   * Prefers the bucket the payload states, because the intensity scale is a
   * per-client fact: Hegic reports 1-5 (the scale its own portal uses) and the
   * numeric rule below is 0-10. That mismatch is why "Severe 5" opened an empty
   * list — the summary counted on one scale and this filtered on the other.
   *
   * The numeric path is left exactly as it was, including its unreachable
   * "minor" branch, for clients that state no severity. Changing it would move
   * Bajaj and Ergo, which this is not the place to do.
   */
  const severityOfPart = (part) => {
    const stated =
      typeof part?.damage_severity === "string"
        ? part.damage_severity.trim().toLowerCase()
        : "";
    if (stated === "severe" || stated === "moderate" || stated === "minor") {
      return stated;
    }

    const intensity = getIntensity(part);
    if (intensity === 0) return null;
    if (intensity > 8) return "severe";
    if (intensity >= 1) return "moderate";
    return "minor";
  };

  // Filter parts based on active filter
  const filterParts = () => {
    let filtered = assessmentDetails;

    console.log("Full assessment data:", assessmentData);
    console.log("Assessment details:", assessmentDetails);
    console.log("Non-identified parts list:", nonIdentifiedPartsList);
    console.log("Sample part:", assessmentDetails[0]);

    // If search is active, search across all parts
    if (isSearchActive && searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();

      // Search in assessment details
      const searchInAssessment = assessmentDetails.filter(
        (part) =>
          part.part_name?.toLowerCase().includes(query) ||
          part.damage_type?.toLowerCase().includes(query) ||
          part.Action?.toLowerCase().includes(query) ||
          part.damage_images?.some((img) =>
            img.dam_type?.toLowerCase().includes(query)
          )
      );

      // Search in non-identified parts
      const searchInNonIdentified = nonIdentifiedPartsList
        .filter((partName) => partName.toLowerCase().includes(query))
        .map((partName) => ({
          part_name: partName,
          damage_images: [],
          part_images: [],
          damage_type: "No damage",
          Action: "No action",
        }));

      // Combine search results
      filtered = [...searchInAssessment, ...searchInNonIdentified];
      return filtered;
    }

    switch (activeFilter) {
      case "damaged":
        filtered = assessmentDetails.filter((part) => {
          // A part is considered damaged if it has any of these indicators:
          return (
            // Has damage images
            (part.damage_images && part.damage_images.length > 0) ||
            // Has damage intensity/severity
            (part.intensity && part.intensity > 0) ||
            (part.Intensity && part.Intensity > 0) ||
            (part.damage_intensity && part.damage_intensity > 0) ||
            // Has damage type
            (part.damage_type && part.damage_type.trim() !== "") ||
            // Has severity indicators
            (part.severity && part.severity.trim() !== "") ||
            (part.damage_severity && part.damage_severity.trim() !== "") ||
            // Has any damage-related field indicating damage
            part.is_damaged === true ||
            part.has_damage === true
          );
        });
        console.log("Damaged parts filter:", {
          totalParts: assessmentDetails.length,
          withDamageImages: filtered.length,
          sampleDamagedPart: filtered[0],
          allDamagedPartsStructure: filtered.map((p) => ({
            part_name: p.part_name,
            intensity: p.intensity,
            Intensity: p.Intensity,
            damage_intensity: p.damage_intensity,
            damage_type: p.damage_type,
            severity: p.severity,
            damage_severity: p.damage_severity,
            damage_images_count: p.damage_images?.length || 0,
            damage_images_sample: p.damage_images?.[0],
            is_damaged: p.is_damaged,
            has_damage: p.has_damage,
            allKeys: Object.keys(p),
          })),
        });

        // Apply severity filter if active (only if we have intensity data)
        if (activeSeverityFilter) {
          filtered = filtered.filter(
            (part) => severityOfPart(part) === activeSeverityFilter,
          );
        }
        break;
      case "undamaged":
        filtered = assessmentDetails.filter(
          (part) => !part.damage_images || part.damage_images.length === 0
        );
        break;
      case "unidentified":
        console.log("=== UNIDENTIFIED FILTER DEBUG ===");
        console.log("nonIdentifiedPartsList:", nonIdentifiedPartsList);
        console.log(
          "nonIdentifiedPartsList.length:",
          nonIdentifiedPartsList.length
        );
        console.log("assessmentData:", assessmentData);
        console.log("assessmentData?.summary:", assessmentData?.summary);
        console.log(
          "assessmentData?.summary?.non_identified_parts_list:",
          assessmentData?.summary?.non_identified_parts_list
        );

        // Use non_identified_parts_list from summary if available, otherwise fallback to old logic
        if (nonIdentifiedPartsList.length > 0) {
          console.log(
            "Using non_identified_parts_list for unidentified filter:",
            nonIdentifiedPartsList
          );
          // Transform string array to objects with part_name property
          filtered = nonIdentifiedPartsList.map((partName) => ({
            part_name: partName,
            damage_images: [],
            part_images: [],
            damage_type: "No damage",
            Action: "No action",
          }));
          console.log("Transformed filtered parts:", filtered);
        } else {
          console.log("Using fallback logic for unidentified filter");
          filtered = assessmentDetails.filter(
            (part) => !part.part_name || part.part_name === "Unknown"
          );
          console.log("Fallback filtered parts:", filtered);
        }
        break;
      default:
        filtered = assessmentDetails;
    }

    return filtered;
  };

  const filteredParts = filterParts();

  // Count parts for each category
  const counts = {
    all: assessmentDetails.length,
    damaged: assessmentDetails.filter((part) => {
      // A part is considered damaged if it has any of these indicators:
      return (
        // Has damage images
        (part.damage_images && part.damage_images.length > 0) ||
        // Has damage intensity/severity
        (part.intensity && part.intensity > 0) ||
        (part.Intensity && part.Intensity > 0) ||
        (part.damage_intensity && part.damage_intensity > 0) ||
        // Has damage type
        (part.damage_type && part.damage_type.trim() !== "") ||
        // Has severity indicators
        (part.severity && part.severity.trim() !== "") ||
        (part.damage_severity && part.damage_severity.trim() !== "") ||
        // Has any damage-related field indicating damage
        part.is_damaged === true ||
        part.has_damage === true
      );
    }).length,
    undamaged: assessmentDetails.filter((part) => {
      // A part is undamaged if it doesn't meet any damage criteria
      return !(
        (part.damage_images && part.damage_images.length > 0) ||
        (part.intensity && part.intensity > 0) ||
        (part.Intensity && part.Intensity > 0) ||
        (part.damage_intensity && part.damage_intensity > 0) ||
        (part.damage_type && part.damage_type.trim() !== "") ||
        (part.severity && part.severity.trim() !== "") ||
        (part.damage_severity && part.damage_severity.trim() !== "") ||
        part.is_damaged === true ||
        part.has_damage === true
      );
    }).length,
    unidentified:
      nonIdentifiedPartsList.length > 0
        ? nonIdentifiedPartsList.length
        : assessmentDetails.filter(
            (part) => !part.part_name || part.part_name === "Unknown"
          ).length,
  };

  // Count severity levels for damaged parts
  const damagedParts = assessmentDetails.filter((part) => {
    // A part is considered damaged if it has any of these indicators:
    return (
      // Has damage images
      (part.damage_images && part.damage_images.length > 0) ||
      // Has damage intensity/severity
      (part.intensity && part.intensity > 0) ||
      (part.Intensity && part.Intensity > 0) ||
      (part.damage_intensity && part.damage_intensity > 0) ||
      // Has damage type
      (part.damage_type && part.damage_type.trim() !== "") ||
      // Has severity indicators
      (part.severity && part.severity.trim() !== "") ||
      (part.damage_severity && part.damage_severity.trim() !== "") ||
      // Has any damage-related field indicating damage
      part.is_damaged === true ||
      part.has_damage === true
    );
  });

  console.log("Severity analysis:", {
    totalDamagedParts: damagedParts.length,
    intensityValues: damagedParts.map((p) => ({
      part_name: p.part_name,
      Intensity: p.Intensity,
      intensity: p.intensity,
      damage_intensity: p.damage_intensity,
      severity: p.severity,
      damage_severity: p.damage_severity,
      intensityType: typeof p.Intensity,
      parsedIntensity: getIntensity(p),
      damage_images_with_intensity: p.damage_images?.map((img) => ({
        Intensity: img.Intensity,
        intensity: img.intensity,
        severity: img.severity,
        damage_intensity: img.damage_intensity,
        keys: Object.keys(img),
      })),
      allPartKeys: Object.keys(p),
    })),
  });

  // Counted with the same rule the filter uses, so a number can never open an
  // empty list.
  const severityCounts = {
    severe: damagedParts.filter((p) => severityOfPart(p) === "severe").length,
    moderate: damagedParts.filter((p) => severityOfPart(p) === "moderate").length,
    minor: damagedParts.filter((p) => severityOfPart(p) === "minor").length,
  };

  // Get severity color - can accept either intensity value or part object
  const getSeverityColor = (intensityOrPart) => {
    let intensity;
    if (typeof intensityOrPart === "object") {
      intensity = getIntensity(intensityOrPart);
    } else {
      intensity = parseInt(intensityOrPart) || 0;
    }

    if (intensity > 8) return "#df3822"; // Severe - red
    if (intensity >= 1) return "#df6a22"; // Moderate - orange
    if (intensity > 0) return "#f8bc05"; // Minor - yellow
    return "#f8bc05"; // Default
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center"
      onClick={handleClose}
    >
      <div
        className={`bg-background-secondary border-t border-border-secondary rounded-tl-[24px] rounded-tr-[24px] w-full h-[85vh] max-h-[85vh] lg:w-[80%] lg:h-[65vh] relative overflow-hidden flex flex-col transition-transform duration-300 ease-out ${
          isAnimating ? "translate-y-0" : "translate-y-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="box-border flex items-center justify-between px-4 py-[8px] border-b border-border-secondary flex-shrink-0">
          <div className="box-border content-center flex flex-wrap gap-[8px] items-center px-0 py-[10px] relative w-full">
            {/* Vehicle Image Thumbnail */}
            {getVehicleImageFromUploads(assessmentData) && (
              <div className="w-[50px] h-[35px] rounded-[6px] overflow-hidden flex-shrink-0 mr-2">
                <img
                  src={getVehicleImageFromUploads(assessmentData)}
                  alt="Vehicle"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}

            {/* Search Section */}
            <div
              className={`flex items-center transition-all duration-300 ease-in-out ${
                isSearchActive ? "w-full lg:w-[60%] flex-none" : "flex-none"
              }`}
            >
              {isSearchActive ? (
                /* Expanded Search Input */
                <div className="flex items-center w-full">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search parts..."
                      className="w-full px-[12px] py-[9px] pl-[40px] pr-[12px] border border-border-primary rounded-[8px] focus:outline-none focus:border-primary text-base"
                      autoFocus
                    />
                    <Search
                      size={20}
                      className="absolute left-[12px] top-1/2 transform -translate-y-1/2 text-[#949ead]"
                    />
                  </div>
                  <button
                    onClick={() => {
                      setIsSearchActive(false);
                      setSearchQuery("");
                    }}
                    className="ml-2 px-[12px] py-[9px] text-[#949ead] hover:text-text-primary transition-colors rounded-[8px] hover:bg-gray-100 border border-border-primary"
                  >
                    <X size={20} />
                  </button>
                </div>
              ) : (
                /* Search Button */
                <button
                  className="cursor-pointer relative rounded-[8px] shrink-0"
                  onClick={() => setIsSearchActive(true)}
                >
                  <div className="box-border content-stretch flex gap-[4px] items-center overflow-clip px-[12px] py-[9px] relative">
                    <Search size={20} className="text-[#949ead]" />
                  </div>
                  <div className="absolute border border-border-dark border-solid inset-0 pointer-events-none rounded-[8px]" />
                </button>
              )}
            </div>

            {/* Filter Tabs - Hidden when search is active */}
            <div
              className={`flex flex-wrap gap-[8px] items-center transition-all duration-300 ease-in-out ${
                isSearchActive ? "hidden" : "opacity-100 scale-100"
              }`}
            >
              {/* All Parts Tab - Active */}
              <div
                className={`${
                  activeFilter === "all"
                    ? "bg-primary box-border content-stretch flex gap-[8px] items-center justify-center px-[12px] py-[8px] relative rounded-[8px] shrink-0 cursor-pointer"
                    : "box-border content-stretch cursor-pointer flex gap-[4px] items-center justify-center overflow-visible px-[12px] py-[8px] relative rounded-[8px] shrink-0"
                }`}
                onClick={() => {
                  setActiveFilter("all");
                  setActiveSeverityFilter(""); // Reset severity filter
                  setIsSearchActive(false); // Reset search state
                  setSearchQuery(""); // Clear search query
                }}
              >
                {activeFilter === "all" && (
                  <div className="absolute border border-primary border-solid inset-0 pointer-events-none rounded-[8px]" />
                )}
                {activeFilter !== "all" && (
                  <div className="absolute border border-border-dark border-solid inset-0 pointer-events-none rounded-[8px]" />
                )}
                <div
                  className={`font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-base text-nowrap tracking-[-0.16px] ${
                    activeFilter === "all" ? "text-white" : "text-[#949ead]"
                  }`}
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[normal] whitespace-pre">All Parts</p>
                </div>
                <div
                  className={`font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-base text-nowrap tracking-[-0.16px] ${
                    activeFilter === "all" ? "text-white" : "text-[#949ead]"
                  }`}
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[normal] whitespace-pre">
                    {counts.all}
                  </p>
                </div>
              </div>

              {/* Unidentified Button */}
              <button
                className="box-border content-stretch cursor-pointer flex gap-[4px] items-center justify-center overflow-visible px-[12px] py-[8px] relative rounded-[8px] shrink-0"
                onClick={() => {
                  setActiveFilter("unidentified");
                  setActiveSeverityFilter(""); // Reset severity filter
                  setIsSearchActive(false); // Reset search state
                  setSearchQuery(""); // Clear search query
                }}
              >
                <div className="absolute border border-border-dark border-solid inset-0 pointer-events-none rounded-[8px]" />
                <div
                  className="font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-[#949ead] text-base text-nowrap tracking-[-0.16px]"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[normal] whitespace-pre">
                    Unidentified
                  </p>
                </div>
                <div
                  className="font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-[#949ead] text-base text-nowrap tracking-[-0.16px]"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[normal] whitespace-pre">
                    {counts.unidentified}
                  </p>
                </div>
              </button>

              {/* Damaged Button - with severity sub-buttons when active */}
              {activeFilter === "damaged" ? (
                <div className="box-border content-stretch flex isolate items-center pl-0 pr-px py-0 relative shrink-0 max-w-full overflow-x-auto cl-scroll">
                  {/* Main Damaged Button */}
                  <div className=" box-border content-stretch flex gap-[8px] items-center justify-center mr-[-1px] px-[12px] py-[8px] relative rounded-bl-[8px] rounded-tl-[8px] shrink-0 z-[4]">
                    <div className="absolute border border-primary border-solid inset-0 pointer-events-none rounded-bl-[8px] rounded-tl-[8px]" />
                    <div
                      className="font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-primary text-base text-nowrap tracking-[-0.16px]"
                      style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                    >
                      <p className="leading-[normal] whitespace-pre">Damaged</p>
                    </div>
                    <div
                      className="font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-primary text-base text-nowrap tracking-[-0.16px]"
                      style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                    >
                      <p className="leading-[normal] whitespace-pre">
                        {counts.damaged}
                      </p>
                    </div>
                  </div>

                  {/* Severe Button */}
                  <div
                    className={`box-border content-stretch flex gap-[8px] items-center justify-center mr-[-1px] px-[12px] py-[8px] relative shrink-0 z-[3] cursor-pointer  ${
                      activeSeverityFilter === "severe"
                        ? "bg-primary"
                        : "bg-background-secondary"
                    }`}
                    onClick={() =>
                      setActiveSeverityFilter(
                        activeSeverityFilter === "severe" ? "" : "severe"
                      )
                    }
                  >
                    <div className="absolute border-border-primary border border-solid inset-0 pointer-events-none" />
                    <div
                      className={`font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-base text-nowrap tracking-[-0.16px] ${
                        activeSeverityFilter === "severe"
                          ? "text-white"
                          : "text-text-primary"
                      }`}
                      style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                    >
                      <p className="leading-[normal] whitespace-pre">Severe</p>
                    </div>
                    <div
                      className={`font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-base text-nowrap tracking-[-0.16px] ${
                        activeSeverityFilter === "severe"
                          ? "text-white"
                          : "text-text-primary"
                      }`}
                      style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                    >
                      <p className="leading-[normal] whitespace-pre">
                        {severityCounts.severe}
                      </p>
                    </div>
                  </div>

                  {/* Moderate Button */}
                  <div
                    className={`box-border content-stretch flex gap-[4px] items-center justify-center mr-[-1px] px-[12px] py-[8px] relative shrink-0 z-[2] cursor-pointer ${
                      activeSeverityFilter === "moderate"
                        ? "bg-primary"
                        : "bg-background-secondary"
                    }`}
                    onClick={() =>
                      setActiveSeverityFilter(
                        activeSeverityFilter === "moderate" ? "" : "moderate"
                      )
                    }
                  >
                    <div className="absolute border-border-primary border border-solid inset-0 pointer-events-none" />
                    <div
                      className={`font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-base text-nowrap tracking-[-0.16px] ${
                        activeSeverityFilter === "moderate"
                          ? "text-white"
                          : "text-text-primary"
                      }`}
                      style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                    >
                      <p className="leading-[normal] whitespace-pre">
                        Moderate
                      </p>
                    </div>
                    <div
                      className={`font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-base text-nowrap tracking-[-0.16px] ${
                        activeSeverityFilter === "moderate"
                          ? "text-white"
                          : "text-text-primary"
                      }`}
                      style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                    >
                      <p className="leading-[normal] whitespace-pre">
                        {severityCounts.moderate}
                      </p>
                    </div>
                  </div>

                  {/* Minor Button */}
                  <div
                    className={`box-border content-stretch flex gap-[4px] items-center justify-center mr-[-1px] px-[12px] py-[8px] relative rounded-br-[8px] rounded-tr-[8px] shrink-0 z-[1] cursor-pointer ${
                      activeSeverityFilter === "minor"
                        ? "bg-primary"
                        : "bg-background-secondary"
                    }`}
                    onClick={() =>
                      setActiveSeverityFilter(
                        activeSeverityFilter === "minor" ? "" : "minor"
                      )
                    }
                  >
                    <div className="absolute border-border-primary border border-solid inset-0 pointer-events-none rounded-br-[8px] rounded-tr-[8px]" />
                    <div
                      className={`font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-base text-nowrap tracking-[-0.16px] ${
                        activeSeverityFilter === "minor"
                          ? "text-white"
                          : "text-text-primary"
                      }`}
                      style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                    >
                      <p className="leading-[normal] whitespace-pre">Minor</p>
                    </div>
                    <div
                      className={`font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-base text-nowrap tracking-[-0.16px] ${
                        activeSeverityFilter === "minor"
                          ? "text-white"
                          : "text-text-primary"
                      }`}
                      style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                    >
                      <p className="leading-[normal] whitespace-pre">
                        {severityCounts.minor}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                /* Regular Damaged Button */
                <button
                  className="box-border content-stretch cursor-pointer flex gap-[4px] items-center justify-center overflow-visible px-[12px] py-[8px] relative rounded-[8px] shrink-0"
                  onClick={() => {
                    setActiveFilter("damaged");
                    setActiveSeverityFilter("severe"); // Set severe as default when switching to damaged
                    setIsSearchActive(false); // Reset search state
                    setSearchQuery(""); // Clear search query
                  }}
                >
                  <div className="absolute border border-border-dark border-solid inset-0 pointer-events-none rounded-[8px]" />
                  <div
                    className="font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-[#949ead] text-base text-nowrap tracking-[-0.16px]"
                    style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                  >
                    <p className="leading-[normal] whitespace-pre">Damaged</p>
                  </div>
                  <div
                    className="font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-[#949ead] text-base text-nowrap tracking-[-0.16px]"
                    style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                  >
                    <p className="leading-[normal] whitespace-pre">
                      {counts.damaged}
                    </p>
                  </div>
                </button>
              )}

              {/* Undamaged Button */}
              <div
                className="box-border content-stretch flex gap-[4px] items-center justify-center px-[12px] py-[8px] relative rounded-[8px] shrink-0 cursor-pointer"
                onClick={() => {
                  setActiveFilter("undamaged");
                  setActiveSeverityFilter(""); // Reset severity filter
                  setIsSearchActive(false); // Reset search state
                  setSearchQuery(""); // Clear search query
                }}
              >
                <div className="absolute border border-border-dark border-solid inset-0 pointer-events-none rounded-[8px]" />
                <div
                  className="font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-[#949ead] text-base text-nowrap tracking-[-0.16px]"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[normal] whitespace-pre">Undamaged</p>
                </div>
                <div
                  className="font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-[#949ead] text-base text-nowrap tracking-[-0.16px]"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[normal] whitespace-pre">
                    {counts.undamaged}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Close Button - Always visible */}
          <button
            className={`block cursor-pointer overflow-clip relative size-[24px] transition-all duration-300 ease-in-out ${
              isSearchActive ? "ml-4" : "ml-0"
            }`}
            onClick={handleClose}
          >
            <X size={24} className="text-black" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4">
          {/* Search Results Header */}
          {isSearchActive && searchQuery.trim() && (
            <div className="py-4 border-b border-border-secondary">
              <p className="text-[#949ead] text-sm">
                {filteredParts.length} result
                {filteredParts.length !== 1 ? "s" : ""} for "{searchQuery}"
              </p>
            </div>
          )}

          {console.log("=== RENDERING DEBUG ===", {
            activeFilter,
            activeSeverityFilter,
            isSearchActive,
            searchQuery,
            filteredPartsCount: filteredParts.length,
            filteredParts: filteredParts,
            nonIdentifiedPartsList: nonIdentifiedPartsList,
            partsWithImages: filteredParts.map((p) => ({
              part_name: p.part_name,
              intensity: getIntensity(p),
              has_part_images: p.part_images?.length > 0,
              has_damage_images: p.damage_images?.length > 0,
              part_images_count: p.part_images?.length || 0,
              damage_images_count: p.damage_images?.length || 0,
            })),
          })}
          {filteredParts.map((part, index) => (
            <div key={index}>
              {/* Conditional rendering based on filter type or search */}
              {activeFilter === "unidentified" && !isSearchActive ? (
                /* Simplified layout for unidentified parts - matching Figma design */
                <button className="box-border content-stretch cursor-pointer flex gap-[12px] items-center justify-center overflow-visible px-[8px] py-[16px] relative shrink-0 w-full">
                  {index > 0 && (
                    <div
                      aria-hidden="true"
                      className="absolute border-border-dark border-[1px_0px_0px] border-solid inset-0 pointer-events-none"
                    />
                  )}
                  <div className="basis-0 content-stretch flex gap-[4px] grow items-center min-h-px min-w-px relative shrink-0">
                    <div className="content-stretch flex gap-[12px] items-center relative shrink-0">
                      <div
                        className="flex flex-col font-['Noto_Sans:SemiBold',_sans-serif] font-semibold justify-end leading-[0] relative shrink-0 text-base text-nowrap text-black tracking-[-0.16px]"
                        style={{
                          fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                        }}
                      >
                        <p className="leading-[normal] whitespace-pre">
                          {part.part_name || "Unknown Part"}
                        </p>
                      </div>
                    </div>
                  </div>
                </button>
              ) : (
                /* Original detailed layout for other filters */
                <>
                  <button className="box-border cursor-pointer flex gap-[12px] items-center justify-center overflow-visible px-[8px] py-[16px] w-full">
                    <div className="basis-0 flex gap-[4px] grow items-center min-h-px min-w-px relative">
                      <div className="flex gap-[12px] items-center relative">
                        {/* Part Name */}
                        <div
                          className="flex flex-col font-['Noto_Sans:SemiBold',_sans-serif] font-semibold justify-end leading-[0] text-base text-nowrap text-black tracking-[-0.16px]"
                          style={{
                            fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                          }}
                        >
                          {part.part_name || "Unknown Part"}
                        </div>

                        {/* Separator */}
                        <div className="flex h-[0px] items-center justify-center relative w-[0px]">
                          <div className="flex-none rotate-[90deg]">
                            <div className="h-0 relative w-[20px]">
                              <div className="absolute bottom-0 left-0 right-0 top-[-1px] border-t border-[#303030]" />
                            </div>
                          </div>
                        </div>

                        {/* Damage Type */}
                        <div
                          className="font-['Noto_Sans:Regular',_sans-serif] font-normal leading-[0] text-base text-nowrap text-black tracking-[-0.16px]"
                          style={{
                            fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                          }}
                        >
                          {damageTypeLabel(part)}
                        </div>

                        {/* Separator */}
                        <div className="flex h-[0px] items-center justify-center relative w-[0px]">
                          <div className="flex-none rotate-[90deg]">
                            <div className="h-0 relative w-[20px]">
                              <div className="absolute bottom-0 left-0 right-0 top-[-1px] border-t border-[#303030]" />
                            </div>
                          </div>
                        </div>

                        {/* Action */}
                        <div
                          className="font-['Noto_Sans:Regular',_sans-serif] font-normal leading-[0] text-base text-nowrap text-black tracking-[-0.16px]"
                          style={{
                            fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                          }}
                        >
                          {partActionLabel(part)}
                        </div>

                        {/* Severity Badge */}
                        {getIntensity(part) > 0 && (
                          <div
                            className="box-border flex flex-col gap-[10px] items-center justify-center px-[6px] py-0 relative rounded-[12px]"
                            style={{
                              backgroundColor: getSeverityColor(part),
                            }}
                          >
                            <div
                              className="font-['Noto_Sans:Display_SemiBold',_sans-serif] font-semibold leading-[0] text-sm text-nowrap text-black"
                              style={{
                                fontVariationSettings: "'CTGR' 100, 'wdth' 100",
                                lineHeight: "20px",
                              }}
                            >
                              {getIntensity(part)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Edit Button */}
                    {!isApproved && (
                      <button
                        className="overflow-clip relative size-[22px] hover:bg-gray-100 rounded transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditPart(part);
                        }}
                      >
                        <Edit size={22} className="text-black" />
                      </button>
                    )}
                  </button>

                  {/* Images Row */}
                  {getPartPreviewImages(part).length > 0 && (
                    <div className="box-border cursor-pointer flex gap-[8px] items-center pb-[24px] pl-[8px] pr-0 pt-0 relative w-full border-b border-border-dark">
                      {/* Damage crops then every other angle, from one list so
                          a click's index matches what the lightbox opens. */}
                      {getPartPreviewImages(part).map((frame, imgIndex) => (
                        <div
                          key={frame.imageUrl}
                          className="h-[120px] relative w-[125px] flex-shrink-0"
                          role="button"
                          tabIndex={0}
                          onClick={() => openImageLightbox(part, imgIndex)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openImageLightbox(part, imgIndex);
                            }
                          }}
                        >
                          <img
                            src={frame.imageUrl}
                            alt={`${part.part_name || "Part"} view ${imgIndex + 1}`}
                            className="absolute inset-0 max-w-none object-50%-50% object-cover pointer-events-none size-full"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}

          {filteredParts.length === 0 && (
            <div className="text-center py-8">
              <p className="text-[#949ead] text-lg">
                No parts found matching the current filter.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Part Modal */}
      <EditPartModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        partData={selectedPart}
        assessmentData={assessmentData}
        onRefreshAssessment={onRefreshAssessment}
      />
      <div onClick={(e) => e.stopPropagation()}>
        <ImageLightbox
          isOpen={lightboxOpen}
          images={lightboxImages}
          currentIndex={lightboxCurrentIndex}
          onClose={closeImageLightbox}
        />
      </div>
    </div>
  );
};

export default DamagedPartsModal;
